import React, { useEffect, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiPost } from '../../services/api';
import { resolveCompanyDestination } from '../../utils/companyRouting';
import GoogleOAuthButton, { AuthDivider } from './GoogleOAuthButton';

export default function Login() {
  const { user, token, authLoading, login, apiBase, isVerified } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showVerifiedAlert, setShowVerifiedAlert] = useState(false);
  const [showResetAlert, setShowResetAlert] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [twoFactorStep, setTwoFactorStep] = useState(null);
  const [pendingEmail, setPendingEmail] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === '1') {
      setShowVerifiedAlert(true);
    }
    if (params.get('reset') === '1') {
      setShowResetAlert(true);
    }
    if (params.get('error') === 'oauth_failed') {
      setError('Google sign-in failed. Please try again or use email and password.');
    }
  }, [location.search]);

  useEffect(() => {
    if (authLoading || !user) return;
    // Unverified users: VerifyEmailPending handles the bounce — do not redirect here
    if (!isVerified) return;
    (async () => {
      const path = await resolveCompanyDestination(apiBase, token, user.role);
      navigate(path, { replace: true });
    })();
  }, [user, authLoading, isVerified, navigate, apiBase, token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    if (!apiBase) {
      setError('Invalid email or password');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiPost('/api/v1/auth/login', { email, password, remember });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setError('Too many login attempts. Please wait a minute and try again.');
        return;
      }
      if (res.status === 401) {
        setError('Invalid email or password');
        return;
      }
      if (res.status === 422 && data.errors) {
        setFieldErrors(data.errors);
        return;
      }
      if (res.status === 403 && data?.data?.email_not_verified) {
        // Set token in context — AuthContext's useEffect will call refreshUser() automatically
        // VerifyEmailPending waits for token+user hydration before deciding to bounce
        login(null, data.data.temp_token);
        navigate('/verify-email-pending');
        return;
      }
      if (!res.ok) {
        setError('Invalid email or password');
        return;
      }

      if (data?.data?.requires_2fa_setup) {
        navigate('/admin/2fa/setup', {
          state: {
            preAuthToken: data.data.pre_auth_token,
          },
        });
        return;
      }

      if (data?.data?.requires_2fa) {
        setPendingEmail(data.data.email || email);
        setTwoFactorStep('verify');
        return;
      }

      const u = data?.data?.user;
      const t = data?.data?.token;
      if (u && t) {
        login(u, t, remember);
        const path = await resolveCompanyDestination(apiBase, t, u.role);
        navigate(path, { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTwoFactorSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await apiPost('/api/v1/auth/2fa/verify', {
        email: pendingEmail,
        code: twoFactorCode,
        remember,
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
        return;
      }

      if (res.status === 422) {
        setError('Invalid or expired code');
        return;
      }

      if (!res.ok) {
        setError('Invalid or expired code');
        return;
      }

      const u = data?.data?.user;
      const t = data?.data?.token;
      if (u && t) {
        login(u, t, remember);
        const path = await resolveCompanyDestination(apiBase, t, u.role);
        navigate(path, { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading) {
    return (
      <div className="rwm-auth bg-white">
        <div className="rwm-auth__card py-5 text-center text-muted">
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rwm-auth bg-white">
      <div className="rwm-auth__card py-5">
      <Card className="border" style={{ borderColor: 'var(--rwm-border)' }}>
        <Card.Body className="p-4">
          <h1 className="h4 fw-semibold mb-4" style={{ color: 'var(--rwm-dark)' }}>
            Log In
          </h1>
          {showVerifiedAlert ? (
            <div className="alert alert-success mb-4" role="alert" data-testid="rwm-login-verified-alert">
              Your email has been verified. Please log in.
            </div>
          ) : null}
          {showResetAlert ? (
            <div className="alert alert-success mb-4" role="alert" data-testid="rwm-login-reset-alert">
              Your password has been reset. Please log in.
            </div>
          ) : null}
          {twoFactorStep === 'verify' ? (
            <Form data-testid="rwm-login-2fa-form" onSubmit={handleTwoFactorSubmit}>
              <p className="text-muted mb-3">
                Enter the 6-digit code from your authenticator app for{' '}
                <strong>{pendingEmail}</strong>.
              </p>
              <Form.Group className="mb-3" controlId="loginTwoFactorCode">
                <Form.Label>Authentication code</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={twoFactorCode}
                  onChange={(ev) => setTwoFactorCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoComplete="one-time-code"
                  data-testid="rwm-login-2fa-code"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="rwm-remember-me-2fa"
                  label="Remember this device for 30 days"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="rwm-login-remember-2fa"
                />
              </Form.Group>
              {error ? (
                <p className="rwm-auth__field-error mb-3" data-testid="rwm-login-error">
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                variant="info"
                className="w-100 text-white btn-lg"
                disabled={submitting || twoFactorCode.length !== 6}
                data-testid="rwm-login-2fa-submit"
              >
                {submitting ? 'Verifying…' : 'Verify'}
              </Button>
            </Form>
          ) : (
          <>
          <GoogleOAuthButton testId="rwm-login-google-btn" apiBase={apiBase} disabled={submitting} />
          <AuthDivider />
          <Form data-testid="rwm-login-form" onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="loginEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(ev) => {
                  setEmail(ev.target.value);
                  setShowVerifiedAlert(false);
                  setShowResetAlert(false);
                }}
                required
                autoComplete="email"
                data-testid="rwm-login-email"
              />
              {fieldErrors.email ? (
                <div className="rwm-auth__field-error mt-1">{fieldErrors.email[0]}</div>
              ) : null}
            </Form.Group>
            <Form.Group className="mb-3" controlId="loginPassword">
              <Form.Label>Password</Form.Label>
              <div className="d-flex gap-2 align-items-center">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(ev) => {
                    setPassword(ev.target.value);
                    setShowVerifiedAlert(false);
                    setShowResetAlert(false);
                  }}
                  required
                  autoComplete="current-password"
                  data-testid="rwm-login-password"
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              </div>
              {fieldErrors.password ? (
                <div className="rwm-auth__field-error mt-1">{fieldErrors.password[0]}</div>
              ) : null}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="rwm-remember-me"
                label="Remember this device for 30 days"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="rwm-login-remember"
              />
            </Form.Group>
            {error ? (
              <p className="rwm-auth__field-error mb-3" data-testid="rwm-login-error">
                {error}
              </p>
            ) : null}
            <Button
              type="submit"
              variant="info"
              className="w-100 text-white btn-lg"
              disabled={submitting}
              data-testid="rwm-login-submit"
            >
              {submitting ? 'Logging in…' : 'Log In'}
            </Button>
          </Form>
          </>
          )}
          <p className="small text-center text-muted mt-3 mb-2">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="rwm-job-detail__browse-link text-decoration-none">
              Sign Up
            </Link>
          </p>
          <p className="small text-center mb-0">
            <Link to="/forgot-password" className="text-muted text-decoration-none">
              Forgot password?
            </Link>
          </p>
        </Card.Body>
      </Card>
      </div>
    </div>
  );
}
